/* ============================================================================
 * YeneHealth — webshop product listing overrides
 *
 * Two things live here:
 *   1. `webshop.ProductView`  — infinite scroll in place of Prev/Next paging.
 *   2. `webshop.ProductGrid`  — the storefront product card, modelled on
 *      yenehealth.com/pharmacy. Styles are in yene/public/css/product_grid.css.
 *
 * Both listing routes (/all-products and the Item Group pages) render through
 * these classes, so the card is defined once here.
 *
 * Contracts that must not be broken when editing the card markup:
 *   - The outer wrapper keeps `.item-card`: `load_more_products` below collects
 *     newly rendered cards with `.find(".item-card")`.
 *   - `.btn-add-to-cart-list`, `.go-to-cart-grid` and `.cart-indicator` must
 *     stay siblings. webshop's `bind_add_to_cart_action` reveals the latter two
 *     via `$btn.parent().find(...)` after a click.
 *   - The wishlist toggle keeps `.like-action`, `data-item-code` and an inner
 *     `.wish-icon` carrying `wished` / `not-wished`; `wishlist_action` reads
 *     and swaps exactly those classes.
 * ========================================================================== */

(function () {
	let view_overridden = false;
	let grid_overridden = false;

	/* The storefront presents products as a grid, while stock webshop opens in
	 * "List View". all-products/index.js reads this key at DOM ready, so seeding
	 * it here changes the default — and only when the visitor has never used the
	 * view toggle, so an explicit choice still wins. */
	try {
		if (!localStorage.getItem("product_view")) {
			localStorage.setItem("product_view", "Grid View");
		}
	} catch (e) {
		// Storage can throw in private mode; the list default is a fine fallback.
	}

	const esc = (value) =>
		frappe.utils && frappe.utils.escape_html
			? frappe.utils.escape_html(value == null ? "" : value)
			: String(value == null ? "" : value).replace(/[&<>"']/g, (c) => ({
				"&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
			}[c]));

	/* Safe for use inside a url('...') in a style attribute. */
	const esc_url = (value) => esc(String(value == null ? "" : value).replace(/["'()\\]/g, encodeURIComponent));

	const route_of = (item) => esc(item.route || "#");

	/* Cash and YeneHealth Credit prices side by side, while credit is offered
	 * (yene.overrides.credit_pricing). `credit_price_enabled` is only set for
	 * vetted users who can see the cash price too. */
	const cash_label_html = (item) =>
		item.credit_price_enabled ? `<span class="yene-price-label">${__("Cash")}</span>` : ``;

	const credit_price_html = (item) => {
		if (!item.credit_price_enabled) return ``;
		if (item.formatted_credit_price) {
			return `<div class="yene-credit-price">
				<span class="yene-price-label">${__("Credit")}</span>
				${esc(item.formatted_credit_price)}
			</div>`;
		}
		return `<div class="yene-credit-price yene-credit-unavailable">${__("Not available on credit")}</div>`;
	};

	function overrideProductView() {
		if (!view_overridden && window.webshop && webshop.ProductView) {
			/* ---- Filters in a modal ----------------------------------------
			 * The listing template puts the filters in a col-md-3 beside the
			 * grid. They are relocated into a modal so the grid can run the
			 * full width of the page.
			 *
			 * `#product-filters` is *moved*, not re-rendered: `bind_filters`
			 * attaches change handlers straight to the inputs (not delegated),
			 * and jQuery keeps handlers attached across a DOM move. Everything
			 * else in views.js addresses the panel by id, so it does not care
			 * where the panel lives.
			 */
			const MODAL_ID = "yene-filter-modal";

			const base_prepare_toolbar = webshop.ProductView.prototype.prepare_toolbar;

			webshop.ProductView.prototype.prepare_toolbar = function () {
				base_prepare_toolbar.call(this);
				this.setup_filter_modal();
				this.render_filter_toggle();
			};

			/* Number of checked filter values, read back from the URL — the
			 * same source views.js uses to restore filter state. */
			webshop.ProductView.prototype.get_active_filter_count = function () {
				const params = frappe.utils.get_query_params();
				let count = 0;

				["field_filters", "attribute_filters"].forEach((key) => {
					if (!params[key]) return;
					try {
						Object.values(JSON.parse(params[key]) || {}).forEach((values) => {
							count += (values || []).length;
						});
					} catch (e) {
						// A hand-edited query string should not break the toolbar.
					}
				});

				return count;
			};

			webshop.ProductView.prototype.setup_filter_modal = function () {
				let $filters = $("#product-filters");
				if (!$filters.length || $("#" + MODAL_ID).length) return;

				$(`
					<div class="modal fade yene-filter-modal" id="${MODAL_ID}" tabindex="-1"
						role="dialog" aria-labelledby="${MODAL_ID}-title" aria-hidden="true">
						<div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
							role="document">
							<div class="modal-content">
								<div class="modal-header">
									<h5 class="modal-title" id="${MODAL_ID}-title">${__("Filters")}</h5>
									<button type="button" class="close" data-dismiss="modal"
										aria-label="${__("Close")}">&times;</button>
								</div>
								<div class="modal-body"></div>
								<div class="modal-footer">
									<a class="yene-filter-clear" href="${location.pathname}">${__("Clear all")}</a>
									<button type="button" class="yene-filter-apply" data-dismiss="modal">
										${__("Show results")}
									</button>
								</div>
							</div>
						</div>
					</div>
				`).appendTo("body");

				// The col-md-3 that held the panel would otherwise stay behind as
				// an empty column and keep a third of the row.
				$filters.parent().addClass("yene-filters-col");

				$filters
					.removeClass("collapse d-md-block mr-4")
					.appendTo(`#${MODAL_ID} .modal-body`);
			};

			webshop.ProductView.prototype.render_filter_toggle = function () {
				if (!$("#" + MODAL_ID).length) return;

				let $toolbar = $("#product-listing .toolbar");
				if (!$toolbar.length) return;

				if (!$toolbar.find(".yene-filter-toggle").length) {
					$toolbar.prepend(`
						<button type="button" class="yene-filter-toggle"
							data-toggle="modal" data-target="#${MODAL_ID}"
							aria-haspopup="dialog">
							<svg class="icon sm" aria-hidden="true"><use href="#icon-filter"></use></svg>
							${__("Filters")}
							<span class="yene-filter-count" hidden></span>
						</button>
					`);
				}

				const $button = $toolbar.find(".yene-filter-toggle");

				/* Item Group pages build their filters from the Item Group doc
				 * rather than Webshop Settings (see ProductFiltersBuilder), so
				 * the panel can legitimately be empty — don't offer a button
				 * that opens an empty dialog. Discount filters arrive with the
				 * items, hence re-checking on every render. */
				const has_filters = $("#product-filters .filter-block").length > 0;
				$button.prop("hidden", !has_filters);
				if (!has_filters) return;

				const count = this.get_active_filter_count();
				$toolbar.find(".yene-filter-count").text(count).prop("hidden", !count);
				$button.toggleClass("is-active", !!count);
			};

			/* ---- Infinite scroll ------------------------------------------
			 * Replaces the Prev/Next buttons. `product_count` is the total
			 * number of matching items (yene.overrides.product_query returns an
			 * absolute count, not one relative to the offset), so it can be
			 * compared against how many rows have been rendered so far.
			 */
			webshop.ProductView.prototype.add_paging_section = function (settings) {
				$(".product-paging-area").remove();

				this.render_result_count();

				if (this.products) {
					this.current_start = 0;
					this.page_length = settings.products_per_page || 12;
					this.loading_more = false;
					this.has_more = this.product_count > this.page_length;

					// Clean up any old messages
					$(".infinite-scroll-done").remove();

					if (!this.scroll_event_bound) {
						this.scroll_event_bound = true;
						let me = this;
						$(window).on("scroll", function () {
							if (!me.has_more || me.loading_more) return;

							let scrollTop = $(window).scrollTop();
							let windowHeight = $(window).height();
							let documentHeight = $(document).height();

							// Trigger when within 400px of the bottom of the page
							if (documentHeight - (scrollTop + windowHeight) < 400) {
								me.load_more_products();
							}
						});
					}
				}
			};

			/* Result count in the toolbar, e.g. "128 products". */
			webshop.ProductView.prototype.render_result_count = function () {
				let $toolbar = $("#product-listing .toolbar");
				if (!$toolbar.length) return;

				let count = this.product_count || 0;
				let label = count === 1 ? __("product") : __("products");

				let $count = $toolbar.find(".yene-result-count");
				if (!$count.length) {
					$count = $('<div class="yene-result-count"></div>').appendTo($toolbar);
				}
				$count.html(`<strong>${esc(count)}</strong> ${esc(label)}`);

				// The grid sits behind the modal, so the footer button carries the
				// result count to show that a filter took effect.
				$(".yene-filter-modal .yene-filter-apply").text(
					count ? __("Show {0} {1}", [count, label]) : __("Show results")
				);
				this.render_filter_toggle();
			};

			// Define load_more_products on ProductView prototype
			webshop.ProductView.prototype.load_more_products = function () {
				let me = this;
				me.loading_more = true;

				// Append loading spinner with text
				let $loader = $(
					'<div class="infinite-scroll-loader">' +
					'<div class="yene-spinner" role="status" aria-hidden="true"></div>' +
					`<span>${__("Loading more products...")}</span>` +
					"</div>"
				);
				$("#product-listing").append($loader);

				let next_start = me.current_start + me.page_length;
				let args = me.get_query_filters();
				args.start = next_start;

				frappe.call({
					method: "webshop.webshop.api.get_product_filter_data",
					args: {
						query_args: args
					},
					callback: function (result) {
						$loader.remove();
						me.loading_more = false;

						if (result && result.message && result.message.items && result.message.items.length) {
							let items = result.message.items;
							let settings = result.message.settings || {};

							me.products = me.products.concat(items);
							me.current_start = next_start;
							me.has_more = me.product_count > (me.current_start + items.length);

							// Append to grid view
							if ($("#products-grid-area").length) {
								let $temp_grid = $("<div>");
								new webshop.ProductGrid({
									items: items,
									products_section: $temp_grid,
									settings: settings,
									preference: me.preference
								});
								$("#products-grid-area").append($temp_grid.find(".item-card"));
							}

							// Append to list view
							if ($("#products-list-area").length) {
								let $temp_list = $("<div>");
								new webshop.ProductList({
									items: items,
									products_section: $temp_list,
									settings: settings,
									preference: me.preference
								});
								$("#products-list-area").append($temp_list.find(".list-row"));
							}
						} else {
							me.has_more = false;
						}

						// Show done state if no more products are available
						if (!me.has_more) {
							if (!$(".infinite-scroll-done").length) {
								$("#product-listing").append(
									`<div class="infinite-scroll-done">${__("All products loaded.")}</div>`
								);
							}
						}
					},
					error: function () {
						$loader.remove();
						me.loading_more = false;
					}
				});
			};

			webshop.ProductView.prototype.bind_paging_action = function () {};

			view_overridden = true;
		}

		if (!grid_overridden && window.webshop && webshop.ProductGrid) {
			/* ---- Storefront product card ---------------------------------- */

			webshop.ProductGrid.prototype.make = function () {
				let me = this;
				let html = ``;

				this.items.forEach((item) => {
					let title = item.web_item_name || item.item_name || item.item_code || "";
					title = title.length > 90 ? title.substr(0, 90) + "..." : title;

					html += `<div class="item-card">
						<div class="card yene-card">
							${me.get_image_html(item, title)}
							${me.get_card_body_html(item, title, me.settings)}
						</div>
					</div>`;
				});

				this.products_section.append(html);
			};

			/* Square media area: image, badges, wishlist and the hover overlay. */
			webshop.ProductGrid.prototype.get_image_html = function (item, title) {
				let route = route_of(item);
				let safe_title = esc(title);

				let visual = item.website_image
					? `<div class="yene-card-img" itemprop="image" role="img"
							aria-label="${safe_title}"
							style="background-image: url('${esc_url(item.website_image)}');"></div>`
					: `<div class="yene-card-img-fallback" itemprop="image" aria-hidden="true">
							${esc(frappe.get_abbr(title))}
						</div>`;

				let badge = item.expiring_this_month
					? `<span class="yene-badge yene-badge-expiring">${__("Expiring this month")}</span>`
					: ``;

				// Only meaningful alongside a struck-through MRP, which is what
				// `item.discount` is populated with.
				let discount = item.formatted_mrp && item.discount
					? `<span class="yene-discount">${esc(item.discount)} ${__("OFF")}</span>`
					: ``;

				let wishlist = !item.has_variants && this.settings.enable_wishlist
					? this.get_wishlist_icon(item)
					: ``;

				return `
					<div class="yene-card-media">
						<a class="yene-card-media-link" href="/${route}" aria-label="${safe_title}">
							${visual}
						</a>
						${badge}
						${discount}
						${wishlist}
						<div class="yene-card-quick">
							<a class="yene-quick-btn" href="/${route}" tabindex="-1">${__("View details")}</a>
						</div>
					</div>
				`;
			};

			webshop.ProductGrid.prototype.get_card_body_html = function (item, title, settings) {
				let route = route_of(item);

				return `
					<div class="card-body yene-card-body">
						<span class="yene-card-cat">${esc(item.item_group || "")}</span>
						<a class="yene-card-title" href="/${route}" itemprop="name">${esc(title || "")}</a>
						${this.get_price_html(item)}
						${this.get_stock_availability(item, settings)}
						${this.get_primary_button(item, settings)}
					</div>
				`;
			};

			webshop.ProductGrid.prototype.get_wishlist_icon = function (item) {
				let icon_class = item.wished ? "wished" : "not-wished";
				let label = item.wished ? __("Remove from wishlist") : __("Add to wishlist");

				return `
					<button type="button"
						class="like-action ${item.wished ? "like-action-wished" : ""}"
						data-item-code="${esc(item.item_code)}"
						aria-label="${esc(label)}" title="${esc(label)}">
						<svg class="icon sm" aria-hidden="true">
							<use class="${icon_class} wish-icon" href="#icon-heart"></use>
						</svg>
					</button>
				`;
			};

			/* Prices are stripped server-side for visitors who are not vetted HCF
			 * users (see yene.overrides.product_query.add_display_details), so the
			 * card shows a locked state instead of an empty gap. */
			webshop.ProductGrid.prototype.get_price_html = function (item) {
				if (!item.formatted_price) {
					return `
						<div class="yene-card-price">
							<span class="yene-price-locked">
								<svg class="icon sm" aria-hidden="true"><use href="#icon-lock"></use></svg>
								${__("Pricing on approval")}
							</span>
						</div>
					`;
				}

				let was = item.formatted_mrp
					? `<span class="yene-price-was">${esc(item.formatted_mrp.replace(/ +/g, ""))}</span>`
					: ``;

				return `
					<div class="yene-card-price" itemprop="offers" itemscope
						itemtype="https://schema.org/AggregateOffer">
						${cash_label_html(item)}
						<span class="yene-price-now">${esc(item.formatted_price)}</span>
						${was}
						${credit_price_html(item)}
					</div>
				`;
			};

			webshop.ProductGrid.prototype.get_stock_availability = function (item, settings) {
				if (!settings.show_stock_availability || item.has_variants) return ``;

				if (item.on_backorder) {
					return `<div class="yene-card-stock yene-stock-backorder">${__("Available on backorder")}</div>`;
				}
				if (!item.in_stock) {
					return `<div class="yene-card-stock yene-stock-out">${__("Out of stock")}</div>`;
				}
				return ``;
			};

			/* `.btn-add-to-cart-list`, `.go-to-cart-grid` and `.cart-indicator`
			 * stay siblings inside `.yene-card-cta` — see the header note. */
			webshop.ProductGrid.prototype.get_primary_button = function (item, settings) {
				let route = route_of(item);

				if (item.has_variants) {
					return `
						<div class="yene-card-cta">
							<a class="yene-cta yene-cta-secondary" href="/${route}">${__("Explore options")}</a>
						</div>
					`;
				}

				if (!settings.enabled) return ``;

				if (!settings.allow_items_not_in_stock && !item.in_stock) {
					return `
						<div class="yene-card-cta">
							<span class="yene-cta yene-cta-disabled" aria-disabled="true">${__("Out of stock")}</span>
						</div>
					`;
				}

				let cart_label = settings.enable_checkout ? __("Add to Cart") : __("Add to Quote");
				let go_label = settings.enable_checkout ? __("Go to Cart") : __("Go to Quote");

				return `
					<div class="yene-card-cta">
						<button type="button"
							class="btn btn-add-to-cart-list yene-cta yene-cta-primary ${item.in_cart ? "hidden" : ""}"
							data-item-code="${esc(item.item_code)}">
							<svg class="icon" aria-hidden="true"><use href="#icon-assets"></use></svg>
							${cart_label}
						</button>
						<a class="go-to-cart-grid yene-cta yene-cta-secondary ${item.in_cart ? "" : "hidden"}"
							href="/cart">
							<svg class="icon" aria-hidden="true"><use href="#icon-assets"></use></svg>
							${go_label}
						</a>
					</div>
				`;
			};

			/* ---- List view row ---------------------------------------------
			 * Stock webshop's `get_item_details` with the credit price added. */
			if (webshop.ProductList) {
				webshop.ProductList.prototype.get_item_details = function (item, settings) {
					let details = `
						<p class="product-code">
							${item.item_group} | ${__("Item Code")} : ${item.item_code}
						</p>
						<div class="mt-2" style="color: var(--gray-600) !important; font-size: 13px;">
							${item.short_description || ""}
						</div>
						<div class="product-price" itemprop="offers" itemscope itemtype="https://schema.org/AggregateOffer">
							${item.formatted_price ? cash_label_html(item) : ""}
							${item.formatted_price || ""}
					`;

					if (item.formatted_mrp) {
						details += `
							<small class="striked-price">
								<s>${item.formatted_mrp ? item.formatted_mrp.replace(/ +/g, "") : ""}</s>
							</small>
							<small class="ml-1 product-info-green">
								${item.discount} ${__("OFF")}
							</small>
						`;
					}

					if (item.formatted_price) {
						details += credit_price_html(item);
					}
					details += this.get_stock_availability(item, settings);
					details += `</div>`;

					return details;
				};
			}

			grid_overridden = true;
		}

		return view_overridden && grid_overridden;
	}

	/* Install order matters. `web_include_js` puts this file ahead of webshop's
	 * own web.bundle.js (app order in sites/apps.txt), so `webshop.ProductView`
	 * does not exist yet at parse time.
	 *
	 * `frappe.ready` callbacks are drained by `frappe.trigger_ready()` from
	 * website.js's DOM-ready handler, which jQuery runs before the inline
	 * `$(() => new ProductListing())` that all-products/index.js emits — so
	 * registering there guarantees the prototypes are patched before the first
	 * listing is constructed. The interval is only a safety net for pages that
	 * build a ProductView outside that flow.
	 */
	if (!overrideProductView()) {
		if (window.frappe && frappe.ready) {
			frappe.ready(overrideProductView);
		}

		let attempts = 0;
		const timer = setInterval(() => {
			if (overrideProductView() || ++attempts > 100) {
				clearInterval(timer);
			}
		}, 100);
	}
})();

// B2C Notice Banner — shown on individual product detail pages
(function() {
    frappe.ready(function() {
        // Inject on product detail pages OR product listing pages
        const isProductDetailPage = document.querySelector('.product-page-content');
        const isProductListingPage = document.querySelector('.product-search-content') || document.querySelector('#product-listing') || document.querySelector('.item-group-content') || document.querySelector('#products-grid-area');
        if (!isProductDetailPage && !isProductListingPage) return;

        // Avoid duplicate injection
        if (document.querySelector('.yene-b2c-notice')) return;

        // Build the notice using safe DOM methods (no innerHTML)
        const notice = document.createElement('div');
        notice.className = 'yene-b2c-notice';

        const iconSpan = document.createElement('span');
        iconSpan.className = 'yene-b2c-notice-icon';
        iconSpan.textContent = '\u2139\uFE0F';
        notice.appendChild(iconSpan);

        const textContainer = document.createElement('div');
        textContainer.className = 'yene-b2c-notice-content';

        const mainText = document.createElement('p');
        mainText.className = 'yene-b2c-notice-title';
        
        const descText = document.createElement('p');
        descText.className = 'yene-b2c-notice-desc';

        // Get user status to decide which message to show
        frappe.call({
            method: 'yene.overrides.vetted_check.get_user_vetted_status',
            callback: function(r) {
                let status = r.message || { is_vetted: false, hcf_status: 'None' };
                let isGuest = frappe.session.user === "Guest";
                
                if (!isGuest && !status.is_vetted) {
                    if (status.hcf_status === "Pending") {
                        mainText.textContent = 'Application Pending';
                        descText.textContent = 'Your Health Care Facilities application is currently under review. You will be able to see pricing and place orders once approved.';
                    } else if (status.hcf_status === "Expired") {
                        // A facility document passed its expiry date (yene.overrides.hcf_documents).
                        mainText.textContent = 'Documents Expired';
                        descText.innerHTML = 'Some of your facility documents have expired. Please <a href="/hcf-registration" style="text-decoration: underline; color: var(--primary);">upload the renewed documents</a> to see pricing and place orders again.';
                    } else {
                        mainText.textContent = 'Action Required';
                        descText.innerHTML = 'Please <a href="/hcf-registration" style="text-decoration: underline; color: var(--primary);">submit the Health Care Facilities form</a> to see pricing and make orders.';
                    }
                } else if (isGuest) {
                    mainText.textContent = 'Bulk & Facilities Pricing';
                    descText.textContent = 'This items and pricing is only for bulk buying and facilities. If you are a customer buying only one item or for personal use, please visit ';
                    
                    const link = document.createElement('a');
                    link.href = 'https://yenehealth.com/pharmacy';
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    link.className = 'yene-b2c-notice-link';
                    link.textContent = 'YeneHealth Pharmacy';
                    descText.appendChild(link);
                    
                    const trailText = document.createTextNode(' for retail purchases.');
                    descText.appendChild(trailText);
                } else {
                    notice.style.display = 'none';
                    return;
                }

                textContainer.appendChild(descText);
                notice.appendChild(textContainer);

                // Determine where to insert the notice
                if (isProductDetailPage) {
                    const productContainer = document.querySelector('.product-container.item-main');
                    if (productContainer && productContainer.parentNode) {
                        productContainer.parentNode.insertBefore(notice, productContainer.nextSibling);
                    } else {
                        const pageContent = document.querySelector('.page_content');
                        if (pageContent) pageContent.appendChild(notice);
                    }
                } else if (isProductListingPage) {
                    // For listing pages, inject it right at the top before the grid
                    const listingContainer = document.querySelector('.product-search-content') || document.querySelector('#product-listing') || document.querySelector('.item-group-content') || document.querySelector('#products-grid-area');
                    if (listingContainer && listingContainer.parentNode) {
                        listingContainer.parentNode.insertBefore(notice, listingContainer);
                    }
                }
            }
        });
    });
})();
